import {Exhibitor} from "@/entities/Exhibitor";
import {isDefined} from "@/utils";
import {Theme} from '@/entities/Theme';

export class ExhibitorContainer {

  private _startLetters: string[] = [];
  private _exhibitorsMap: Record<string, Exhibitor[]> = {};
  private _filteredExhibitors: Exhibitor[] = [];
  private readonly _exhibitors: Exhibitor[] = [];
  private _exhibitorsAfterSearchAndFilter: Exhibitor[] = [];
  private readonly _themes: Theme[] = []
  private readonly _sortRegex = new RegExp('[A-Z]');

  constructor(entities: Exhibitor[], themes: Theme[]) {
    this._exhibitorsAfterSearchAndFilter = entities;
    this.createExhibitorsMap(entities);
    this._exhibitors = entities;
    this._themes = themes;
    this.createStartLetters(entities);
  }

  private createStartLetters(entities: Exhibitor[]): void{
    const letterSet = new Set<string>();
    for (const exhibitor of entities) {
      let firstChar = exhibitor.sortOrder[0].toUpperCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
      if (!this._sortRegex.test(firstChar)) {
        firstChar = '#';
      }
      letterSet.add(firstChar);
    }
    this._startLetters = [...letterSet];
  }

  private createExhibitorsMap(entities: Exhibitor[]): void {
    this._exhibitorsMap = {}
    this._filteredExhibitors = entities;

    for (const exhibitor of entities) {
      let firstChar = exhibitor.sortOrder[0].toUpperCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
      if (!this._sortRegex.test(firstChar)) {
        firstChar = '#';
      }
      if (!isDefined(this._exhibitorsMap[firstChar])) {
        this._exhibitorsMap[firstChar] = [];
      }
      this._exhibitorsMap[firstChar].push(exhibitor);
    }
  }

  public getExhibitorsMap(pageSize: number): Record<string, Exhibitor[]> {
    const copy = {};
    if (pageSize <= 0) {
      return {};
    }
    for (let [key, value] of Object.entries(this._exhibitorsMap)) {
      if (value.length > pageSize) {
        copy[key] = value.slice(0, pageSize);
        break;
      } else {
        copy[key] = value;
        pageSize = pageSize - value.length;
        if (pageSize <= 0) {
          break;
        }
      }
    }
    return copy;
  }


  get startLetters(): string[] {
    return this._startLetters;
  }

  get exhibitors(): Exhibitor[] {
    return this._exhibitors;
  }


  get themes(): Theme[] {
    return this._themes;
  }

  public letterExistsInCurrentList(letter : string): boolean{
    for(const exhibitor of this._exhibitorsAfterSearchAndFilter){
      let firstChar = exhibitor.sortOrder[0].toUpperCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
      if (!this._sortRegex.test(firstChar)) {
        firstChar = '#';
      }
      if(firstChar === letter){
        return true;
      }
    }
    return false;
  }

  public hasMorePages(pageSize): boolean {
    return this._filteredExhibitors.length > pageSize;
  }

  public filterExhibitors(searchValue: string, filters: Record<string, string>, startLetter: string): void {
    let filteredExhibitors: Exhibitor[];
    searchValue = searchValue.trim().toLowerCase();
    if (searchValue.length > 0) {
      filteredExhibitors = this.applyFilters(filters, this._exhibitors);
      filteredExhibitors = filteredExhibitors.filter(e => {
        return e.hasSearchValue(searchValue);
      });
      filteredExhibitors = this.applyFilters(filters, filteredExhibitors);
    } else {
      filteredExhibitors = this.applyFilters(filters, this._exhibitors);
    }
    this._exhibitorsAfterSearchAndFilter = filteredExhibitors;
    if (startLetter !== 'all') {
      filteredExhibitors = filteredExhibitors.filter(exhibitor => {
        let firstChar = exhibitor.sortOrder[0].toUpperCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
        if (!this._sortRegex.test(firstChar)) {
          firstChar = '#';
        }
        return firstChar === startLetter;
      })
    }
    this.createExhibitorsMap(filteredExhibitors);
  }

  private applyFilters(filters: Record<string, string>, exhibitors: Exhibitor[]): Exhibitor[] {
    if (isDefined(filters) && Object.entries(filters).length > 0) {
      for (const [key, value] of Object.entries(filters)) {
        exhibitors = exhibitors.filter(e => {
          if (isDefined(e.themes)) {
            return e.themes.indexOf(value) > -1;
          }
          return false;
        });
      }
    }
    return exhibitors;
  }

  public hasEntries(): boolean {
    return JSON.stringify(this._exhibitorsMap) !== JSON.stringify({});
  }

}
